// src/components/admin/RatingJobMonitor.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import toast from 'react-hot-toast';

const RatingJobMonitor = ({ jobId, onComplete, onClose, onCancel, onRetry }) => {
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [pollInterval, setPollInterval] = useState(3000); // Start with 3 seconds
  const maxRetries = 3;

  // Request notification permission when component mounts
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          console.log('Notification permission granted');
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!jobId) return;

    const checkJobStatus = async () => {
      try {
        const response = await apiService.getRatingJobStatus(jobId);
        setJobStatus(response.data);
        
        // If job is completed, get results and notify parent
        if (response.data.status === 'completed') {
          const resultsResponse = await apiService.getRatingJobResults(jobId);
          
          // Show browser notification if supported and permission granted
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Resume Rating Completed!', {
              body: `Successfully rated ${response.data.total_resumes} resumes`,
              icon: '/favicon.ico',
              badge: '/favicon.ico'
            });
          }
          
          onComplete(resultsResponse.data);
        } else if (response.data.status === 'failed') {
          setError(response.data.error_message || 'Rating job failed');
          
          // Show browser notification for failure
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Resume Rating Failed!', {
              body: response.data.error_message || 'Rating job failed',
              icon: '/favicon.ico',
              badge: '/favicon.ico'
            });
          }
        }
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.message || 'Failed to check job status';
        console.error('Job status check failed:', {
          error: err,
          status: err.response?.status,
          data: err.response?.data,
          jobId: jobId,
          retryCount: retryCount
        });
        
        // Retry on network errors
        if (!err.response && retryCount < maxRetries) {
          setRetryCount(prev => prev + 1);
          setTimeout(() => checkJobStatus(), 1000 * (retryCount + 1)); // Exponential backoff
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    // Check status immediately
    checkJobStatus();

    // Set up polling for in-progress jobs
    const interval = setInterval(() => {
      if (!jobStatus || !['completed', 'failed'].includes(jobStatus.status)) {
        checkJobStatus();
      } else {
        // Job is completed or failed, stop polling
        clearInterval(interval);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [jobId, jobStatus?.status, onComplete]);

  const handleCancel = async () => {
    if (!jobId) return;
    
    setIsProcessing(true);
    try {
      await apiService.cancelRatingJob(jobId);
      toast.success('Rating job cancelled successfully');
      
      // Refresh job status
      const response = await apiService.getRatingJobStatus(jobId);
      setJobStatus(response.data);
      
      if (onCancel) onCancel(jobId);
    } catch (error) {
      toast.error(error.message || 'Failed to cancel job');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetrySync = async () => {
    if (!jobId) return;
    
    setIsProcessing(true);
    try {
      const result = await apiService.retryRatingJobSync(jobId);
      toast.success(`Job retried successfully! ${result.sync_result?.data?.successful || 0} resumes processed.`);
      
      if (onRetry) onRetry(jobId, result);
      if (onComplete) onComplete(result.sync_result?.data);
    } catch (error) {
      toast.error(error.message || 'Failed to retry job');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Checking job status...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || jobStatus?.status === 'failed') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">❌</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Rating Failed</h3>
            <p className="text-gray-600 mb-4">
              {error || jobStatus?.error_message || 'Rating job failed'}
            </p>
            
            <div className="flex space-x-2 justify-center">
              {jobStatus?.status === 'failed' && (
                <button
                  onClick={handleRetrySync}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {isProcessing ? 'Retrying...' : 'Retry (Sync)'}
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!jobStatus) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'in_progress': return '🔄';
      case 'completed': return '✅';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600';
      case 'in_progress': return 'text-blue-600';
      case 'completed': return 'text-green-600';
      case 'failed': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="text-4xl mb-4">{getStatusIcon(jobStatus.status)}</div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Resume Rating in Progress
          </h3>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Please click "Continue in Background" and check after some time. 
              Don't keep this modal open to avoid excessive server requests.
            </p>
          </div>
          
          <p className={`text-sm font-medium mb-4 ${getStatusColor(jobStatus.status)}`}>
            Status: {jobStatus.status.replace('_', ' ').toUpperCase()}
          </p>

          {jobStatus.status === 'in_progress' && (
            <div className="mb-4">
              <div className="bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${jobStatus.progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">
                {jobStatus.processed_resumes}/{jobStatus.total_resumes} resumes processed ({jobStatus.progress}%)
              </p>
            </div>
          )}

          {jobStatus.status === 'completed' && (
            <div className="mb-4">
              <p className="text-sm text-green-600 font-medium">
                ✓ All {jobStatus.total_resumes} resumes have been rated successfully!
              </p>
            </div>
          )}

          <div className="text-xs text-gray-500 mb-4">
            <p>Job ID: {jobStatus.id}</p>
            <p>Started: {new Date(jobStatus.started_at).toLocaleString()}</p>
            {jobStatus.completed_at && (
              <p>Completed: {new Date(jobStatus.completed_at).toLocaleString()}</p>
            )}
          </div>

          {jobStatus.status === 'completed' && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              View Results
            </button>
          )}

          {['pending', 'in_progress'].includes(jobStatus.status) && (
            <div className="flex space-x-2 justify-center">
              <button
                onClick={handleCancel}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
              >
                {isProcessing ? 'Cancelling...' : 'Cancel Job'}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Continue in Background
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatingJobMonitor;